(function(window, document){
  'use strict';

  if(window.MoonasEHA3D){
    window.MoonasEHA3D.boot(document);
    return;
  }

  var THREE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
  var TAU = Math.PI * 2;
  var COPPER = 0xB06A33;
  var GOLD = 0xC2902A;
  var PAPER = 0xFAF6EC;
  var TEAL = 0x0D98BA;
  var SOIL = 0x5B3A24;
  var instances = [];
  var waitingObservers = [];
  var threeCallbacks = [];
  var threeLoading = false;
  var warned = false;

  function clamp(value, min, max){
    return Math.max(min, Math.min(max, value));
  }

  function smoothstep(value){
    var clean = clamp(value, 0, 1);
    return clean * clean * (3 - 2 * clean);
  }

  function warnOnce(error){
    if(warned) return;
    warned = true;
    if(window.console && console.warn) console.warn('Moonas EHA 3D fallback:', error);
  }

  function disposeMaterial(material){
    if(!material) return;
    Object.keys(material).forEach(function(key){
      var value = material[key];
      if(value && value.isTexture && value.dispose) value.dispose();
    });
    if(material.dispose) material.dispose();
  }

  function disposeObject(object){
    if(!object) return;
    object.traverse(function(child){
      if(child.geometry && child.geometry.dispose) child.geometry.dispose();
      if(Array.isArray(child.material)) child.material.forEach(disposeMaterial);
      else if(child.material) disposeMaterial(child.material);
    });
    if(object.parent) object.parent.remove(object);
  }

  function webGLAvailable(){
    if(!window.WebGLRenderingContext) return false;
    try {
      var probe = document.createElement('canvas');
      return !!(probe.getContext('webgl') || probe.getContext('experimental-webgl'));
    } catch(ignore){
      return false;
    }
  }

  function motionAllowed(){
    return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function detailAllowed(){
    return window.innerWidth >= 700;
  }

  function flushThree(error){
    var callbacks = threeCallbacks.slice();
    threeCallbacks.length = 0;
    callbacks.forEach(function(callback){ callback(error); });
  }

  function ensureThree(callback){
    if(window.THREE && window.THREE.REVISION === '128'){
      callback(null);
      return;
    }
    threeCallbacks.push(callback);
    if(threeLoading) return;
    threeLoading = true;
    var script = document.createElement('script');
    script.src = THREE_URL;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.referrerPolicy = 'no-referrer';
    script.onload = function(){
      threeLoading = false;
      if(window.THREE) flushThree(null);
      else flushThree(new Error('Three.js did not initialize.'));
    };
    script.onerror = function(){
      threeLoading = false;
      flushThree(new Error('Three.js could not be loaded.'));
    };
    document.head.appendChild(script);
  }

  function EHAHero(canvas, opts){
    if(!canvas || !canvas.getContext) throw new Error('A canvas element is required.');
    if(!window.THREE) throw new Error('Three.js is not available.');

    this.canvas = canvas;
    this.opts = opts || {};
    this.THREE = window.THREE;
    this.stage = canvas.closest('[data-eha-3d-stage]') || canvas.parentNode;
    this.root = canvas.closest('[data-eha-3d-root]') || this.stage;
    this.scrollEnd = this.root.querySelector('[data-eha-3d-scroll-end]') || this.root;
    this.hotspots = Array.prototype.slice.call(this.stage.querySelectorAll('[data-eha-hotspot]'));
    this.hotspotAnchors = {};
    this.disposed = false;
    this.failed = false;
    this.visible = true;
    this.pageVisible = !document.hidden;
    this.frameId = 0;
    this.lastFrame = 0;
    this.targetProgress = 0;
    this.currentProgress = 0;
    this.idleRotation = 0;
    this.dragRotation = 0;
    this.targetDragRotation = 0;
    this.dragging = false;
    this.pointerStartX = 0;
    this.pointerStartRotation = 0;
    this.mobileDetail = window.innerWidth < 860;

    this._frame = this._frame.bind(this);
    this._resize = this._resize.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);

    try {
      this._build();
      this._resize();
      this._onScroll();
      this.renderer.render(this.scene, this.camera);
      this._projectHotspots();
      this.stage.classList.add('is-eha-3d');
      this._connectLifecycle();
      this._requestFrame();
    } catch(error){
      this.dispose();
      throw error;
    }
  }

  EHAHero.prototype._build = function(){
    var THREE = this.THREE;
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    this.renderer.setClearColor(PAPER, 0);
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(31, 1, 0.1, 40);
    this.camera.position.set(0, 2.55, 6.45);
    this.cameraLook = new THREE.Vector3(0, 2.45, 0);
    this.camera.lookAt(this.cameraLook);

    this.antenna = new THREE.Group();
    this.antenna.rotation.x = -0.025;
    this.scene.add(this.antenna);
    this.soilGroup = new THREE.Group();
    this.scene.add(this.soilGroup);

    this._buildEnvironment();
    this._buildLights();
    this._buildRod();
    this._buildCrown();
    this._buildUpperCoil();
    this._buildSoil();
    this._buildFieldLines();

    this.hotspotAnchors.crown = new THREE.Vector3(0.56, 2.86, 0.08);
    this.hotspotAnchors.rod = new THREE.Vector3(0.1, -0.05, 0.08);
    this.hotspotAnchors.soil = new THREE.Vector3(0.45, -3.02, 0.18);
    this.projectedPoint = new THREE.Vector3();
  };

  EHAHero.prototype._buildEnvironment = function(){
    var THREE = this.THREE;
    var envCanvas = document.createElement('canvas');
    envCanvas.width = 128;
    envCanvas.height = 64;
    var ctx = envCanvas.getContext('2d');
    var gradient = ctx.createLinearGradient(0, 0, 0, envCanvas.height);
    gradient.addColorStop(0, '#fff7dd');
    gradient.addColorStop(0.42, '#d8aa72');
    gradient.addColorStop(0.7, '#6f9898');
    gradient.addColorStop(1, '#2b1d15');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, envCanvas.width, envCanvas.height);
    var glow = ctx.createRadialGradient(34, 17, 2, 34, 17, 34);
    glow.addColorStop(0, 'rgba(255,255,255,.98)');
    glow.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, envCanvas.width, envCanvas.height);

    this.environmentTexture = new THREE.CanvasTexture(envCanvas);
    this.environmentTexture.mapping = THREE.EquirectangularReflectionMapping;
    this.environmentTexture.encoding = THREE.sRGBEncoding;
    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    this.pmrem.compileEquirectangularShader();
    this.environmentTarget = this.pmrem.fromEquirectangular(this.environmentTexture);
    this.scene.environment = this.environmentTarget.texture;
    this.environmentTexture.dispose();
    this.environmentTexture = null;
    this.pmrem.dispose();
    this.pmrem = null;
  };

  EHAHero.prototype._buildLights = function(){
    var THREE = this.THREE;
    this.scene.add(new THREE.HemisphereLight(0xfff4d8, 0x355f62, 1.42));
    var key = new THREE.DirectionalLight(0xffd49b, 3.25);
    key.position.set(4.8, 7, 5.5);
    this.scene.add(key);
    var rim = new THREE.DirectionalLight(TEAL, 1.08);
    rim.position.set(-5, 2, -4);
    this.scene.add(rim);
    var fill = new THREE.PointLight(0xfff8e9, 1.2, 15);
    fill.position.set(-2.5, 0.5, 4);
    this.scene.add(fill);
  };

  EHAHero.prototype._copperMaterial = function(roughness){
    return new this.THREE.MeshStandardMaterial({
      color: COPPER,
      metalness: 1,
      roughness: roughness == null ? 0.34 : roughness,
      envMapIntensity: 1.42
    });
  };

  EHAHero.prototype._goldMaterial = function(opacity){
    return new this.THREE.MeshStandardMaterial({
      color: GOLD,
      metalness: 1,
      roughness: 0.3,
      transparent: opacity != null && opacity < 1,
      opacity: opacity == null ? 1 : opacity,
      envMapIntensity: 1.5,
      depthWrite: opacity == null || opacity >= 1
    });
  };

  EHAHero.prototype._buildRod = function(){
    var THREE = this.THREE;
    var segments = this.mobileDetail ? 10 : 16;
    var rod = new THREE.Mesh(
      new THREE.CylinderGeometry(0.072, 0.105, 6.05, segments),
      this._copperMaterial(0.34)
    );
    rod.position.y = -0.18;
    this.antenna.add(rod);

    var collar = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.12, 0.34, segments),
      this._copperMaterial(0.3)
    );
    collar.position.y = 2.48;
    this.antenna.add(collar);

    var ridgeCount = this.mobileDetail ? 5 : 8;
    for(var i = 0; i < ridgeCount; i++){
      var ridge = new THREE.Mesh(
        new THREE.TorusGeometry(0.096 + (i % 2) * 0.006, 0.009, 5, this.mobileDetail ? 22 : 32),
        this._goldMaterial(0.72)
      );
      ridge.rotation.x = Math.PI / 2;
      ridge.position.y = 1.18 - i * 0.48 + Math.sin(i * 2.1) * 0.025;
      this.antenna.add(ridge);
    }
  };

  EHAHero.prototype._scaleGeometry = function(){
    var THREE = this.THREE;
    var geometry = new THREE.BufferGeometry();
    var vertices = new Float32Array([
      -0.15,  0.14, 0.00,
       0.00,  0.20, 0.045,
       0.15,  0.14, 0.00,
      -0.16, -0.01, 0.025,
       0.00, -0.08, 0.075,
       0.16, -0.01, 0.025,
       0.00, -0.23, 0.018
    ]);
    var indices = [0,3,1, 1,3,4, 1,4,5, 1,5,2, 3,6,4, 4,6,5];
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  };

  EHAHero.prototype._buildCrown = function(){
    var THREE = this.THREE;
    var crown = new THREE.Group();
    crown.position.y = 2.63;
    this.antenna.add(crown);

    var rows = this.mobileDetail ? 5 : 7;
    var columns = this.mobileDetail ? 10 : 14;
    var count = rows * columns;
    var scaleMaterial = this._copperMaterial(0.39);
    scaleMaterial.side = THREE.DoubleSide;
    scaleMaterial.vertexColors = false;
    var scales = new THREE.InstancedMesh(this._scaleGeometry(), scaleMaterial, count);
    var dummy = new THREE.Object3D();
    var color = new THREE.Color();
    var index = 0;
    for(var row = 0; row < rows; row++){
      var rowRatio = rows === 1 ? 0.5 : row / (rows - 1);
      var y = -0.48 + rowRatio * 1.03;
      var radius = 0.43 + Math.sin(rowRatio * Math.PI) * 0.2;
      for(var col = 0; col < columns; col++){
        var offset = row % 2 ? 0.5 : 0;
        var angle = TAU * (col + offset) / columns;
        var irregular = Math.sin((row + 1) * 17.13 + col * 9.71);
        dummy.position.set(
          Math.sin(angle) * (radius + irregular * 0.014),
          y + Math.cos(col * 3.4 + row) * 0.012,
          Math.cos(angle) * (radius + irregular * 0.014)
        );
        dummy.rotation.set(-0.08 + rowRatio * 0.2, angle + irregular * 0.035, irregular * 0.075);
        var scale = 0.88 + Math.sin(col * 1.9 + row * 2.3) * 0.055;
        dummy.scale.set(scale, scale * 0.92, 1);
        dummy.updateMatrix();
        scales.setMatrixAt(index, dummy.matrix);
        color.setHex(index % 5 === 0 ? GOLD : COPPER);
        color.offsetHSL(0, 0, irregular * 0.035);
        scales.setColorAt(index, color);
        index++;
      }
    }
    scales.instanceMatrix.needsUpdate = true;
    if(scales.instanceColor) scales.instanceColor.needsUpdate = true;
    crown.add(scales);

    var ringYs = [-0.53, -0.16, 0.2, 0.58];
    var ringRadii = [0.44, 0.63, 0.61, 0.42];
    for(var i = 0; i < ringYs.length; i++){
      var ring = new THREE.Mesh(
        new THREE.TorusGeometry(ringRadii[i], i === 1 || i === 2 ? 0.045 : 0.035, this.mobileDetail ? 6 : 8, this.mobileDetail ? 54 : 82),
        i % 2 ? this._goldMaterial(0.86) : this._copperMaterial(0.31)
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.y = ringYs[i];
      crown.add(ring);
    }

    var cap = new THREE.Mesh(
      new THREE.ConeGeometry(0.26, 0.42, this.mobileDetail ? 10 : 16),
      this._goldMaterial(0.92)
    );
    cap.position.y = 0.78;
    crown.add(cap);
  };

  EHAHero.prototype._upperHelix = function(){
    var THREE = this.THREE;
    class HelixCurve extends THREE.Curve {
      getPoint(t, target){
        var angle = TAU * 11 * t;
        var radius = 0.17 + Math.sin(t * Math.PI) * 0.045;
        var result = target || new THREE.Vector3();
        return result.set(Math.cos(angle) * radius, 1.43 + t * 0.98, Math.sin(angle) * radius);
      }
    }
    return new HelixCurve();
  };

  EHAHero.prototype._buildUpperCoil = function(){
    var THREE = this.THREE;
    var geometry = new THREE.TubeGeometry(
      this._upperHelix(),
      this.mobileDetail ? 120 : 220,
      0.027,
      this.mobileDetail ? 5 : 7,
      false
    );
    this.antenna.add(new THREE.Mesh(geometry, this._goldMaterial(0.96)));
  };

  EHAHero.prototype._buildSoil = function(){
    var THREE = this.THREE;
    var dome = new THREE.Mesh(
      new THREE.SphereGeometry(1.58, this.mobileDetail ? 24 : 38, this.mobileDetail ? 10 : 16, 0, TAU, 0, Math.PI / 2),
      new THREE.MeshStandardMaterial({color: SOIL, roughness: 0.97, metalness: 0, envMapIntensity: 0.18})
    );
    dome.position.y = -3.2;
    dome.scale.y = 0.38;
    this.soilGroup.add(dome);

    var ground = new THREE.Mesh(
      new THREE.PlaneGeometry(5.2, 5.2, 1, 1),
      new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        uniforms: {groundColor: {value: new THREE.Color(PAPER)}},
        vertexShader: 'varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }',
        fragmentShader: 'varying vec2 vUv; uniform vec3 groundColor; void main(){ float d=distance(vUv,vec2(.5)); float a=1.0-smoothstep(.18,.5,d); gl_FragColor=vec4(groundColor,a*.72); }'
      })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -3.205;
    this.soilGroup.add(ground);
  };

  EHAHero.prototype._buildFieldLines = function(){
    var THREE = this.THREE;
    var field = new THREE.Group();
    field.position.y = 2.7;
    this.antenna.add(field);
    for(var i = 0; i < 3; i++){
      var line = new THREE.Mesh(
        new THREE.TorusGeometry(1.04 + i * 0.19, 0.007, 4, this.mobileDetail ? 68 : 112),
        this._goldMaterial(0.075)
      );
      line.material.depthWrite = false;
      line.rotation.x = Math.PI / 2 + (i - 1) * 0.34;
      line.rotation.z = (i - 1) * 0.48;
      field.add(line);
    }
  };

  EHAHero.prototype._connectLifecycle = function(){
    var self = this;
    if('ResizeObserver' in window){
      this.resizeObserver = new ResizeObserver(function(){ self._resize(); });
      this.resizeObserver.observe(this.canvas);
    } else {
      window.addEventListener('resize', this._resize);
    }
    if('IntersectionObserver' in window){
      this.visibilityObserver = new IntersectionObserver(function(entries){
        entries.forEach(function(entry){
          self.visible = entry.isIntersecting;
          if(self.visible) self._requestFrame();
          else self._cancelFrame();
        });
      }, {threshold: 0.01, rootMargin: '120px 0px'});
      this.visibilityObserver.observe(this.canvas);
    }
    document.addEventListener('visibilitychange', this._onVisibility);
    window.addEventListener('scroll', this._onScroll, {passive: true});
    window.addEventListener('resize', this._onScroll, {passive: true});
    this.canvas.style.touchAction = 'pan-y';
    this.canvas.addEventListener('pointerdown', this._onPointerDown);
    window.addEventListener('pointermove', this._onPointerMove, {passive: true});
    window.addEventListener('pointerup', this._onPointerUp, {passive: true});
    window.addEventListener('pointercancel', this._onPointerUp, {passive: true});
  };

  EHAHero.prototype._onVisibility = function(){
    this.pageVisible = !document.hidden;
    if(this.pageVisible && this.visible) this._requestFrame();
    else this._cancelFrame();
  };

  EHAHero.prototype._onScroll = function(){
    if(this.disposed) return;
    var heroRect = this.stage.getBoundingClientRect();
    var endRect = this.scrollEnd.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight || 1;
    var start = window.pageYOffset + heroRect.top - viewport * 0.08;
    var narrativeFinish = window.pageYOffset + endRect.bottom - viewport * 0.58;
    var visibleTravel = Math.min(heroRect.height * 0.9, viewport * 0.78);
    var finish = Math.min(narrativeFinish, start + visibleTravel);
    var travel = Math.max(viewport * 0.52, finish - start);
    this.targetProgress = clamp((window.pageYOffset - start) / travel, 0, 1);
    this._requestFrame();
  };

  EHAHero.prototype._onPointerDown = function(event){
    if(event.pointerType === 'mouse' && event.button !== 0) return;
    this.dragging = true;
    this.pointerStartX = event.clientX;
    this.pointerStartRotation = this.targetDragRotation;
    if(this.canvas.setPointerCapture){
      try { this.canvas.setPointerCapture(event.pointerId); } catch(ignore) {}
    }
  };

  EHAHero.prototype._onPointerMove = function(event){
    if(!this.dragging) return;
    this.targetDragRotation = clamp(this.pointerStartRotation + (event.clientX - this.pointerStartX) * 0.0055, -0.62, 0.62);
  };

  EHAHero.prototype._onPointerUp = function(){
    this.dragging = false;
  };

  EHAHero.prototype._resize = function(){
    if(this.disposed || !this.renderer) return;
    var width = Math.max(1, this.canvas.clientWidth || this.stage.clientWidth || 1);
    var height = Math.max(1, this.canvas.clientHeight || this.stage.clientHeight || 1);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this._requestFrame();
  };

  EHAHero.prototype._projectHotspots = function(){
    if(!this.hotspots.length || !this.projectedPoint) return;
    var self = this;
    var width = this.canvas.clientWidth || 1;
    var height = this.canvas.clientHeight || 1;
    var progress = this.currentProgress;
    this.hotspots.forEach(function(label){
      var name = label.getAttribute('data-eha-hotspot');
      var anchor = self.hotspotAnchors[name];
      if(!anchor) return;
      self.projectedPoint.copy(anchor);
      if(name !== 'soil') self.antenna.localToWorld(self.projectedPoint);
      self.projectedPoint.project(self.camera);
      var inFrame = self.projectedPoint.z > -1 && self.projectedPoint.z < 1 && Math.abs(self.projectedPoint.x) < 1.12 && Math.abs(self.projectedPoint.y) < 1.12;
      var inBeat = name === 'crown' ? progress < 0.5 : (name === 'rod' ? progress > 0.12 && progress < 0.82 : progress > 0.52);
      label.style.left = ((self.projectedPoint.x * 0.5 + 0.5) * width) + 'px';
      label.style.top = ((-self.projectedPoint.y * 0.5 + 0.5) * height) + 'px';
      label.style.opacity = inFrame && inBeat ? '1' : '0';
      label.style.visibility = inFrame && inBeat ? 'visible' : 'hidden';
    });
  };

  EHAHero.prototype._requestFrame = function(){
    if(this.disposed || this.failed || this.frameId || !this.visible || !this.pageVisible) return;
    this.frameId = window.requestAnimationFrame(this._frame);
  };

  EHAHero.prototype._cancelFrame = function(){
    if(this.frameId){
      window.cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }
  };

  EHAHero.prototype._frame = function(time){
    this.frameId = 0;
    if(this.disposed || this.failed || !this.visible || !this.pageVisible) return;
    try {
      var delta = this.lastFrame ? Math.min(50, time - this.lastFrame) : 16;
      this.lastFrame = time;
      this.currentProgress += (this.targetProgress - this.currentProgress) * 0.065;
      this.dragRotation += (this.targetDragRotation - this.dragRotation) * 0.08;
      if(!this.dragging) this.targetDragRotation *= 0.982;
      this.idleRotation += delta * 0.000038;
      this.antenna.rotation.y = this.idleRotation + this.dragRotation + this.currentProgress * TAU * 0.38;

      var eased = smoothstep(this.currentProgress);
      var cameraY = 2.55 + (-1.32 - 2.55) * eased;
      var cameraZ = 6.45 + (7.35 - 6.45) * eased;
      var lookY = 2.45 + (-2.05 - 2.45) * eased;
      this.camera.position.y += (cameraY - this.camera.position.y) * 0.065;
      this.camera.position.z += (cameraZ - this.camera.position.z) * 0.065;
      this.cameraLook.y += (lookY - this.cameraLook.y) * 0.065;
      this.camera.lookAt(this.cameraLook);

      this.renderer.render(this.scene, this.camera);
      this._projectHotspots();
      this._requestFrame();
    } catch(error){
      this._fail(error);
    }
  };

  EHAHero.prototype._fail = function(error){
    if(this.failed || this.disposed) return;
    this.failed = true;
    this.dispose();
    warnOnce(error);
  };

  EHAHero.prototype.setScrollProgress = function(progress){
    if(this.disposed) return;
    this.targetProgress = clamp(Number(progress) || 0, 0, 1);
    this._requestFrame();
  };

  EHAHero.prototype.dispose = function(){
    if(this.disposed) return;
    this.disposed = true;
    this._cancelFrame();
    document.removeEventListener('visibilitychange', this._onVisibility);
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onScroll);
    window.removeEventListener('resize', this._resize);
    this.canvas.removeEventListener('pointerdown', this._onPointerDown);
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
    if(this.resizeObserver) this.resizeObserver.disconnect();
    if(this.visibilityObserver) this.visibilityObserver.disconnect();
    if(this.scene) disposeObject(this.scene);
    if(this.environmentTarget) this.environmentTarget.dispose();
    if(this.environmentTexture) this.environmentTexture.dispose();
    if(this.pmrem) this.pmrem.dispose();
    if(this.renderer){
      this.renderer.dispose();
      if(this.renderer.forceContextLoss) this.renderer.forceContextLoss();
    }
    if(this.stage) this.stage.classList.remove('is-eha-3d');
    this.hotspots.forEach(function(label){
      label.style.opacity = '0';
      label.style.visibility = 'hidden';
    });
    this.canvas.removeAttribute('data-eha-3d-active');
    this.scene = null;
    this.renderer = null;
  };

  function initCanvas(canvas){
    if(!canvas || canvas.getAttribute('data-eha-3d-active') === 'true') return;
    if(!detailAllowed() || !motionAllowed() || !webGLAvailable()) return;
    canvas.setAttribute('data-eha-3d-active', 'true');
    ensureThree(function(error){
      if(error){
        canvas.removeAttribute('data-eha-3d-active');
        warnOnce(error);
        return;
      }
      try {
        var hero = new EHAHero(canvas);
        canvas.__moonasEHA3D = hero;
        instances.push(hero);
      } catch(initError){
        canvas.removeAttribute('data-eha-3d-active');
        warnOnce(initError);
      }
    });
  }

  function observeCanvas(canvas){
    if(!canvas || canvas.getAttribute('data-eha-3d-waiting') === 'true' || canvas.getAttribute('data-eha-3d-active') === 'true') return;
    canvas.setAttribute('data-eha-3d-waiting', 'true');
    if('IntersectionObserver' in window){
      var observer = new IntersectionObserver(function(entries){
        entries.forEach(function(entry){
          if(!entry.isIntersecting) return;
          observer.disconnect();
          canvas.removeAttribute('data-eha-3d-waiting');
          initCanvas(canvas);
        });
      }, {rootMargin: '150% 0px', threshold: 0.01});
      observer.observe(canvas);
      waitingObservers.push(observer);
    } else {
      canvas.removeAttribute('data-eha-3d-waiting');
      initCanvas(canvas);
    }
  }

  function boot(scope){
    var context = scope && scope.querySelectorAll ? scope : document;
    if(!detailAllowed() || !motionAllowed() || !webGLAvailable()) return;
    Array.prototype.forEach.call(context.querySelectorAll('[data-eha-3d]'), observeCanvas);
    if(context.matches && context.matches('[data-eha-3d]')) observeCanvas(context);
  }

  function disposeWithin(scope){
    var context = scope && scope.querySelectorAll ? scope : document;
    var canvases = context.querySelectorAll('[data-eha-3d]');
    Array.prototype.forEach.call(canvases, function(canvas){
      if(canvas.__moonasEHA3D){
        canvas.__moonasEHA3D.dispose();
        canvas.__moonasEHA3D = null;
      }
    });
    instances = instances.filter(function(instance){ return !instance.disposed; });
  }

  window.MoonasEHA3D = {
    init: function(canvas, opts){ return new EHAHero(canvas, opts || {}); },
    boot: boot,
    dispose: disposeWithin
  };

  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function(){ boot(document); }, {once: true});
  else boot(document);

  document.addEventListener('shopify:section:load', function(event){ boot(event.target); });
  document.addEventListener('shopify:section:unload', function(event){ disposeWithin(event.target); });
})(window, document);
